import './JournalEditorItem.css';

import { useState } from 'react';

import EditorPreview from '@/components/common/editor-preview/EditorPreview';
import EditorRedact from '@/components/common/editor-redact/EditorRedact';
import ErrorComponent from '@/components/common/error-component/ErrorComponent';
import Popup from '@/components/common/popup/Popup';
import Arrow from '@/components/ui/icons/Arrow';
import Check from '@/components/ui/icons/Check';
import CrossFill from '@/components/ui/icons/CrossFill';
import Delete from '@/components/ui/icons/Delete';
import Redact from '@/components/ui/icons/Redact';
import { ApiDataType } from '@/types';

import classes from './JournalItem.module.scss';

interface JournalItemProps {
	note: {
		author: {
			first_name: string;
			id: number;
			last_name: string;
			photo: string | null;
			position: string;
		};
		date: string;
		id: number;
		project: number;
		text: string;
		error?: string[];
	};
	redaktPermit: boolean;
	redactCallback: (
		noteId: number,
		action: string,
		text?: string
	) => Promise<ApiDataType<string, string[]> | undefined>;
}

const JournalItem = ({
	note,
	redactCallback,
	redaktPermit,
}: JournalItemProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [isRedact, setIsRedact] = useState(false);
	const [isConfirmBlock, setIsConfirmBlock] = useState(false);
	const [value, setValue] = useState(note.text);
	const [error, setError] = useState<string[] | null>(null);

	const confirm = async (id: number, action: string) => {
		const res = await redactCallback(id, action);
		if (res?.errorMessage.length) {
			setError(res.errorMessage);
		}
	};

	const save = async (id: number, action: string) => {
		const res = await redactCallback(id, action, value);
		if (res?.errorMessage.length) {
			setError(res.errorMessage);
		} else {
			setIsRedact(false);
		}
	};

	return (
		<div
			className={
				isOpen && !isConfirmBlock
					? [
							classes.entry,
							classes['entry--open'],
							'editor-wrapper--open',
						].join(' ')
					: isOpen && isConfirmBlock
						? [
								classes.entry,
								classes['entry--open'],
								classes['entry--confirm'],
							].join(' ')
						: classes.entry
			}
		>
			<p className={classes['entry-title']}>
				<span className={classes['entry-title__date']}>
					{note.date.split(',')[0]}
				</span>
				<span className={classes['entry-title__user']}>
					{note.author.first_name} {note.author.last_name}
				</span>
			</p>
			<div
				className={[
					classes.editor,
					'journal-item-editor',
					'editor-global',
				].join(' ')}
			>
				{isRedact ? (
					<EditorRedact
						initialValue={value}
						withActions
						onChange={(newValue) => {
							setValue(newValue);
						}}
						onSave={() => save(note.id, 'redact')}
						onCancel={() => {
							setIsRedact(false);
						}}
					/>
				) : (
					<EditorPreview value={value} open={isOpen} />
				)}
			</div>
			{!isRedact && !isConfirmBlock && (
				<button
					title={isOpen ? 'Свернуть' : 'Подробнее'}
					type='button'
					className={classes['arrow__button']}
					onClick={() => {
						setIsOpen(!isOpen);
						setIsRedact(false);
						setIsConfirmBlock(false);
					}}
				>
					<Arrow clazz={classes['arrow__icon']} />
				</button>
			)}
			{isOpen && !isRedact && (
				<div className={classes.controls}>
					{isConfirmBlock ? (
						<div className={classes.confirm}>
							<button
								type='button'
								title='Подтвердить удаление'
								className={classes.check}
								onClick={() => confirm(note.id, 'delete')}
							>
								<Check clazz={classes.check__icon} />
							</button>
							<button
								type='button'
								title='Отмена'
								className={classes.cross}
								onClick={() => setIsConfirmBlock(false)}
							>
								<CrossFill clazz={classes.cross__icon} />
							</button>
						</div>
					) : (
						redaktPermit && (
							<div className={classes.change}>
								<button
									type='button'
									title='Редактировать'
									className={classes.redact}
									onClick={() => setIsRedact(true)}
								>
									<Redact clazz={classes.redact__icon} />
								</button>
								<button
									type='button'
									title='Удалить'
									className={classes.delete}
									onClick={() => setIsConfirmBlock(true)}
								>
									<Delete clazz={classes.delete__icon} />
								</button>
							</div>
						)
					)}
				</div>
			)}
			{error?.length && (
				<Popup clazz={classes.popup} clickCallback={setError}>
					{error.map((i, idx) => {
						return <ErrorComponent key={idx}>{i}</ErrorComponent>;
					})}
				</Popup>
			)}
		</div>
	);
};

export default JournalItem;
